import React, { useEffect, useState } from "react";
import {
  Calendar,
  Clock,
  ArrowRight,
  ArrowLeft,
  BookOpen,
  Search,
  Sparkles,
  Dice1,
  Dice6,
} from "lucide-react";
import { getPosts, urlFor } from "../sanityClient";

export default function BlogList({ onSelectPost, onBackToHome, onViewDashboard }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    let isMounted = true;
    async function fetchPosts() {
      try {
        setLoading(true);
        const data = await getPosts();
        if (isMounted) {
          setPosts(data || []);
          setError(null);
        }
      } catch (err) {
        console.error("Failed to load blog posts from Sanity:", err);
        if (isMounted) {
          setError(err.message || "Failed to load articles.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    fetchPosts();
    return () => {
      isMounted = false;
    };
  }, []);

  // Collect unique categories
  const allCategories = [
    "all",
    ...new Set(
      posts
        .flatMap((p) => p.categories || [])
        .filter(Boolean)
    ),
  ];

  // Filter posts
  const filteredPosts = posts.filter((post) => {
    const matchesSearch =
      post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" ||
      (post.categories && post.categories.includes(selectedCategory));
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-100">
      {/* Blog Top Navbar */}
      <nav className="border-b border-gray-800/80 bg-gray-950/70 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-18 flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onBackToHome}
              className="inline-flex items-center gap-1.5 text-sm text-gray-200 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Home</span>
            </button>
            <div className="h-4 w-px bg-gray-800" />
            <div
              onClick={onBackToHome}
              className="flex items-center gap-2 cursor-pointer"
            >
              <div className="flex gap-1">
                <Dice1 className="h-5 w-5 text-blue-400" />
                <Dice6 className="h-5 w-5 text-purple-400" />
              </div>
              <span className="text-lg font-bold text-white tracking-tight">
                Casino<span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">Figures</span>
              </span>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20 ml-1">
                Insights
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onViewDashboard}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition-all"
            >
              Live Dashboard
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative py-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-medium mb-4">
          <Sparkles className="h-3.5 w-3.5" />
          Gaming Industry Research & Intelligence
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
          Casino Figures <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400">Insights</span>
        </h1>
        <p className="text-lg text-gray-200 max-w-2xl mx-auto mb-8">
          Deep-dive analysis, quarterly revenue breakdowns, and authoritative data on commercial gaming markets.
        </p>

        {/* Search & Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search reports and topics..."
              className="w-full bg-gray-900/80 border border-gray-800 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all"
            />
          </div>
        </div>

        {/* Categories */}
        {allCategories.length > 1 && (
          <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
            {allCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`text-xs px-3.5 py-1.5 rounded-full font-medium capitalize transition-all ${
                  selectedCategory === cat
                    ? "bg-purple-600 text-white shadow-md shadow-purple-500/20"
                    : "bg-gray-800/80 text-gray-400 hover:text-white hover:bg-gray-800"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-sm text-gray-400">Loading analysis reports...</p>
          </div>
        ) : error ? (
          <div className="max-w-lg mx-auto p-6 rounded-2xl bg-red-950/20 border border-red-500/30 text-center">
            <p className="text-red-400 font-medium mb-2">Unable to load articles</p>
            <p className="text-xs text-gray-400">{error}</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="max-w-lg mx-auto p-10 rounded-2xl bg-gray-900/40 border border-gray-800/80 text-center">
            <BookOpen className="h-10 w-10 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">
              {posts.length === 0 ? "No Articles Published Yet" : "No Matching Articles"}
            </h3>
            <p className="text-sm text-gray-400 mb-6">
              {posts.length === 0
                ? "Your blog is connected to Sanity! Write your first report in Sanity Studio to see it appear here."
                : "Try adjusting your search terms or selecting another category."}
            </p>
            {posts.length === 0 && (
              <div className="p-4 rounded-xl bg-gray-950/70 border border-gray-800 text-left text-xs font-mono text-gray-300">
                <span className="text-purple-400 font-semibold">Tip:</span> Run{" "}
                <code className="bg-gray-800 px-1 py-0.5 rounded">cd studio && npm run dev</code> or{" "}
                <code className="bg-gray-800 px-1 py-0.5 rounded">npx sanity deploy</code> to publish
                posts.
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => {
              const imageUrl = post.mainImage ? urlFor(post.mainImage)?.width(800)?.height(450)?.url() : null;
              const formattedDate = post.publishedAt
                ? new Date(post.publishedAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : null;

              return (
                <article
                  key={post._id}
                  onClick={() => onSelectPost(post.slug.current)}
                  className="group flex flex-col rounded-2xl bg-gray-900/60 border border-gray-800/80 hover:border-purple-500/40 hover:bg-gray-900 transition-all duration-300 overflow-hidden cursor-pointer shadow-lg hover:shadow-purple-500/5"
                >
                  {/* Cover Image */}
                  <div className="relative aspect-video w-full overflow-hidden bg-gray-800">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/20 to-blue-900/20">
                        <BookOpen className="h-10 w-10 text-gray-700" />
                      </div>
                    )}
                    {post.categories && post.categories[0] && (
                      <span className="absolute top-3 left-3 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-md bg-gray-950/80 backdrop-blur-md text-purple-300 border border-gray-800">
                        {post.categories[0]}
                      </span>
                    )}
                  </div>

                  {/* Body Details */}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                      {formattedDate && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {formattedDate}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        5 min read
                      </span>
                    </div>

                    <h2 className="text-xl font-bold text-white group-hover:text-purple-300 transition-colors mb-2 line-clamp-2">
                      {post.title}
                    </h2>

                    {post.excerpt && (
                      <p className="text-sm text-gray-400 line-clamp-3 mb-6 flex-1 leading-relaxed">
                        {post.excerpt}
                      </p>
                    )}

                    {/* Author & CTA */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-800/80 mt-auto">
                      <div className="flex items-center gap-2">
                        {post.author?.image ? (
                          <img
                            src={urlFor(post.author.image)?.width(64)?.height(64)?.url()}
                            alt={post.author.name}
                            className="w-6 h-6 rounded-full object-cover border border-purple-500/30"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-300 text-[10px] font-bold flex items-center justify-center">
                            {post.author?.name ? post.author.name.charAt(0) : "C"}
                          </div>
                        )}
                        <span className="text-xs font-medium text-gray-300">
                          {post.author?.name || "Casino Figures Analyst"}
                        </span>
                      </div>

                      <span className="text-xs font-semibold text-purple-400 group-hover:text-purple-300 inline-flex items-center gap-1 transition-colors">
                        Read
                        <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
